import React, { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/common/Header';
import Footer from '../components/common/Footer';
import MobileStickyBar from '../components/common/MobileStickyBar';
import { useAuth } from '../context/AuthContext';
import { PROFILE_DISCOUNTS } from '../config/discounts';
import { toast } from 'sonner';
import {
  ShoppingBag, Search, Package, Star, AlertCircle,
  Minus, Plus, Trash2, ChevronRight, Tag, Percent, ShoppingCart, X, Check
} from 'lucide-react';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

export default function BoutiquePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [boutiqueActive, setBoutiqueActive] = useState(true);
  const [search, setSearch] = useState('');
  const [cart, setCart] = useState([]);  // panier boutique local
  const [showCart, setShowCart] = useState(false);
  const [clientInfo, setClientInfo] = useState({ nom: '', email: '', telephone: '', adresse: '' });
  const [checkingOut, setCheckingOut] = useState(false);

  const clientProfile = user?.profile || 'particulier';
  const profileCfg = PROFILE_DISCOUNTS[clientProfile];

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      // Vérifier si boutique active
      const settingsRes = await fetch(`${BACKEND}/api/parametres`);
      const settings = await settingsRes.json();
      const active = settings.boutique_active !== false; // true par défaut
      setBoutiqueActive(active);
      if (!active) { setLoading(false); return; }

      let url = `${BACKEND}/api/boutique/produits?limit=60`;
      if (search) url += `&search=${encodeURIComponent(search)}`;
      const res = await fetch(url);
      const data = await res.json();
      setProducts(data.products || []);
    } catch (err) { if (process.env.NODE_ENV !== 'production') console.error(err); }
    setLoading(false);
  }, [search]);

  useEffect(() => {
    if (user) {
      setClientInfo(info => ({
        ...info,
        nom: user.prenom ? `${user.prenom} ${user.nom || ''}`.trim() : (user.nom || ''),
        email: user.email || '',
        telephone: user.telephone || '',
        adresse: user.adresse_personnelle || '',
      }));
    }
  }, [user]);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Gestion panier boutique
  function addToCart(product) {
    setCart(prev => {
      const existing = prev.find(i => i.product_id === product.id);
      if (existing) {
        const newQty = existing.quantite + 1;
        const stockDispo = product.stock_disponible;
        if (stockDispo !== null && stockDispo !== undefined && newQty > stockDispo) {
          toast.error(`Stock maximum atteint (${stockDispo} disponibles)`);
          return prev;
        }
        return prev.map(i => i.product_id === product.id ? { ...i, quantite: newQty } : i);
      }
      return [...prev, {
        product_id: product.id,
        nom: product.nom,
        image: product.images?.[0] || '',
        prix_unitaire: product.tarifs?.[clientProfile] || product.tarifs?.particulier || 0,
        prix_public: product.tarifs?.particulier || 0,
        quantite: 1,
        stock_disponible: product.stock_disponible,
      }];
    });
    toast.success(`${product.nom} ajouté au panier`);
  }

  function updateCartQty(product_id, delta) {
    setCart(prev => prev.map(i => {
      if (i.product_id !== product_id) return i;
      const newQty = Math.max(1, i.quantite + delta);
      if (i.stock_disponible !== null && i.stock_disponible !== undefined && newQty > i.stock_disponible) {
        toast.error('Stock maximum atteint');
        return i;
      }
      return { ...i, quantite: newQty };
    }));
  }

  function removeFromCart(product_id) {
    setCart(prev => prev.filter(i => i.product_id !== product_id));
  }

  const cartTotal = cart.reduce((s, i) => s + i.prix_unitaire * i.quantite, 0);
  const cartCount = cart.reduce((s, i) => s + i.quantite, 0);
  const cartSavings = cart.reduce((s, i) => s + (i.prix_public - i.prix_unitaire) * i.quantite, 0);

  async function handleCheckout() {
    if (cart.length === 0) return;
    if (!clientInfo.email) { toast.error('Votre email est requis pour commander'); return; }
    if (!clientInfo.nom) { toast.error('Votre nom est requis'); return; }
    if (!clientInfo.telephone) { toast.error('Votre téléphone est requis pour commander'); return; }
    if (!clientInfo.adresse) { toast.error("Votre adresse de livraison est requise"); return; }
    setCheckingOut(true);
    try {
      const res = await fetch(`${BACKEND}/api/boutique/commander`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: cart.map(i => ({ product_id: i.product_id, quantite: i.quantite })),
          client_nom: clientInfo.nom,
          client_email: clientInfo.email,
          client_telephone: clientInfo.telephone,
          client_adresse: clientInfo.adresse,
          profile: clientProfile,
          origin_url: window.location.origin,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erreur commande');
      window.location.href = data.url;
    } catch (err) {
      toast.error(err.message || 'Erreur lors du checkout');
    }
    setCheckingOut(false);
  }

  function getStockBadge(product) {
    const s = product.stock_disponible;
    if (s === null || s === undefined) return null;
    if (s === 0) return <Badge variant="destructive" className="text-xs">Rupture</Badge>;
    if (s <= 3) return <Badge className="text-xs" style={{ background: '#C58B19', color: '#fff' }}>Plus que {s} en stock</Badge>;
    return null;
  }

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      <Header />

      <div className="container-ose" style={{ paddingTop: '2rem', paddingBottom: '6rem' }}>
        {/* Header section */}
        <div className="mb-8">
          <span className="inline-block px-3 py-1.5 rounded-full text-xs font-semibold mb-3"
            style={{ background: 'var(--teal-mist)', color: '#0B4B4E' }}>
            Boutique événementielle
          </span>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="font-display text-3xl font-bold mb-1" style={{ color: 'var(--ink)', letterSpacing: '-0.02em' }}>
                Boutique
              </h1>
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>
                Articles disponibles à l'achat immédiat pour compléter votre événement
              </p>
            </div>
            {/* Bouton panier flottant */}
            {cartCount > 0 && (
              <button
                onClick={() => setShowCart(true)}
                data-testid="boutique-cart-btn"
                className="relative flex items-center gap-2 px-4 py-2.5 rounded-xl font-semibold text-sm transition-transform hover:scale-105"
                style={{ background: 'var(--teal)', color: '#fff', boxShadow: '0 4px 16px rgba(93,188,191,0.3)' }}>
                <ShoppingCart size={18} />
                Panier · {cartCount} article{cartCount > 1 ? 's' : ''}
                <span className="font-bold">{cartTotal.toFixed(2)}€</span>
              </button>
            )}
          </div>
        </div>

        {/* Badge remise profil */}
        {profileCfg && profileCfg.pct > 0 && (
          <div className="flex items-center gap-2 px-4 py-3 rounded-xl mb-6"
            style={{ background: profileCfg.bg, border: `1px solid ${profileCfg.color}25` }}>
            <Percent size={15} style={{ color: profileCfg.color }} />
            <p className="text-sm font-semibold" style={{ color: profileCfg.color }}>
              Tarif {profileCfg.label} : -{profileCfg.pct}% appliqué automatiquement sur tous les prix
            </p>
          </div>
        )}

        {/* Recherche */}
        <div className="relative mb-6 max-w-sm">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--text-muted)' }} />
          <Input
            placeholder="Rechercher un article…"
            value={search}
            onChange={e => setSearch(e.target.value)}
            className="pl-9"
            data-testid="boutique-search"
          />
        </div>

        {/* Grille produits */}
        {!boutiqueActive ? (
          <div className="text-center py-24">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ background: 'rgba(197,139,25,0.1)', border: '2px solid rgba(197,139,25,0.3)' }}>
              <ShoppingBag size={36} style={{ color: '#C58B19' }} />
            </div>
            <h2 className="font-display text-2xl font-bold mb-2" style={{ color: 'var(--ink)' }}>
              Boutique temporairement fermée
            </h2>
            <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>
              Notre boutique est en cours de mise à jour. Revenez bientôt !
            </p>
            <Link to="/catalogue"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm"
              style={{ background: 'var(--teal)', color: '#fff' }}>
              Voir nos locations <ChevronRight size={16} />
            </Link>
          </div>
        ) : loading ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="rounded-2xl overflow-hidden" style={{ border: '1px solid var(--border)' }}>
                <Skeleton className="w-full h-48" />
                <div className="p-4 space-y-2"><Skeleton className="h-4 w-3/4" /><Skeleton className="h-6 w-1/2" /></div>
              </div>
            ))}
          </div>
        ) : products.length === 0 ? (
          <div className="text-center py-20">
            <Package size={48} className="mx-auto mb-4" style={{ color: 'var(--text-muted)' }} />
            <p className="text-lg font-semibold mb-1" style={{ color: 'var(--ink)' }}>Aucun article trouvé</p>
            <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Essayez un autre terme de recherche</p>
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {products.map(p => {
              const prix = p.tarifs?.[clientProfile] || p.tarifs?.particulier || 0;
              const prixPublic = p.tarifs?.particulier || 0;
              const hasDiscount = profileCfg?.pct > 0 && prix < prixPublic;
              const inCart = cart.find(i => i.product_id === p.id);
              const rupture = p.stock_disponible === 0;

              return (
                <div key={p.id} data-testid={`boutique-product-${p.id}`}
                  className="rounded-2xl overflow-hidden flex flex-col transition-shadow hover:shadow-md"
                  style={{ background: '#fff', border: '1px solid var(--border)', opacity: rupture ? 0.6 : 1 }}>
                  <Link to={`/produit/${p.id}`} className="relative">
                    <img
                      src={p.images?.[0] || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=400&q=70'}
                      alt={p.nom}
                      className="w-full h-48 object-cover hover:scale-105 transition-transform duration-300"
                      loading="lazy"
                    />
                    {/* Badge rupture / stock faible */}
                    {getStockBadge(p) && (
                      <div className="absolute top-2 right-2">{getStockBadge(p)}</div>
                    )}
                    {p.featured && (
                      <div className="absolute top-2 left-2">
                        <Badge className="text-xs" style={{ background: 'rgba(212,175,55,0.9)', color: '#5C4500' }}>
                          <Star size={10} className="mr-1" fill="currentColor" /> Bestseller
                        </Badge>
                      </div>
                    )}
                    {hasDiscount && (
                      <div className="absolute bottom-2 left-2">
                        <Badge className="text-xs font-bold" style={{ background: profileCfg.color, color: '#fff' }}>
                          -{profileCfg.pct}%
                        </Badge>
                      </div>
                    )}
                  </Link>

                  <div className="p-4 flex flex-col flex-1">
                    <Link to={`/produit/${p.id}`}>
                      <h3 className="font-semibold text-sm mb-2 line-clamp-2 hover:text-[var(--teal)]" style={{ color: 'var(--ink)' }}>{p.nom}</h3>
                    </Link>

                    <div className="mt-auto">
                      {/* Prix */}
                      <div className="flex items-center gap-2 mb-3">
                        {hasDiscount ? (
                          <>
                            <span className="text-lg font-bold price-gold">{prix.toFixed(2)}€</span>
                            <span className="text-xs line-through" style={{ color: 'var(--text-muted)' }}>{prixPublic.toFixed(2)}€</span>
                          </>
                        ) : (
                          <span className="text-lg font-bold price-gold">{prix.toFixed(2)}€</span>
                        )}
                      </div>

                      {/* CTA */}
                      {rupture ? (
                        <Button disabled className="w-full text-xs" variant="outline">
                          <AlertCircle size={13} className="mr-1" /> Rupture de stock
                        </Button>
                      ) : inCart ? (
                        <div className="flex items-center justify-between gap-2">
                          <div className="flex items-center gap-1 px-2 py-1 rounded-lg" style={{ border: '1px solid var(--border)' }}>
                            <button onClick={() => updateCartQty(p.id, -1)} className="w-7 h-7 flex items-center justify-center rounded" style={{ color: 'var(--text-muted)' }}><Minus size={13} /></button>
                            <span className="font-bold text-sm w-6 text-center">{inCart.quantite}</span>
                            <button onClick={() => updateCartQty(p.id, 1)} className="w-7 h-7 flex items-center justify-center rounded" style={{ color: 'var(--teal)' }}><Plus size={13} /></button>
                          </div>
                          <button onClick={() => removeFromCart(p.id)} className="p-2 rounded-lg" style={{ color: '#e53e3e', border: '1px solid #e53e3e22' }}>
                            <Trash2 size={13} />
                          </button>
                          <div className="flex-1 text-right">
                            <span className="text-xs font-semibold" style={{ color: 'var(--teal)' }}>
                              {(prix * inCart.quantite).toFixed(2)}€
                            </span>
                          </div>
                        </div>
                      ) : (
                        <Button
                          onClick={() => addToCart(p)}
                          data-testid={`add-to-boutique-cart-${p.id}`}
                          className="w-full text-xs font-semibold"
                          style={{ background: 'var(--teal)', color: '#fff' }}
                        >
                          <ShoppingBag size={13} className="mr-1" /> Ajouter au panier
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* ── Panneau panier latéral ── */}
      {showCart && (
        <div className="fixed inset-0 z-50 flex justify-end" style={{ backdropFilter: 'blur(4px)', background: 'rgba(0,0,0,0.4)' }}
          onClick={e => { if (e.target === e.currentTarget) setShowCart(false); }}>
          <div className="w-full max-w-md h-full overflow-y-auto flex flex-col"
            style={{ background: 'var(--bg)', boxShadow: '-8px 0 32px rgba(0,0,0,0.15)' }}>
            {/* Header panier */}
            <div className="flex items-center justify-between p-5" style={{ borderBottom: '1px solid var(--border)' }}>
              <div className="flex items-center gap-2">
                <ShoppingCart size={20} style={{ color: 'var(--teal)' }} />
                <h2 className="font-bold text-lg" style={{ color: 'var(--ink)' }}>Votre commande</h2>
                <Badge style={{ background: 'var(--teal)', color: '#fff' }}>{cartCount}</Badge>
              </div>
              <button onClick={() => setShowCart(false)} className="p-2 rounded-lg" style={{ color: 'var(--text-muted)' }}><X size={20} /></button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              {/* Articles */}
              {cart.map(item => (
                <div key={item.product_id} className="flex gap-3 p-3 rounded-xl" style={{ background: '#fff', border: '1px solid var(--border)' }}>
                  {item.image && <img src={item.image} alt={item.nom} className="w-16 h-16 rounded-lg object-cover flex-shrink-0" />}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-sm truncate" style={{ color: 'var(--ink)' }}>{item.nom}</p>
                    <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{item.prix_unitaire.toFixed(2)}€/unité</p>
                    <div className="flex items-center gap-2 mt-2">
                      <div className="flex items-center gap-1 px-2 py-1 rounded-lg" style={{ border: '1px solid var(--border)' }}>
                        <button onClick={() => updateCartQty(item.product_id, -1)} className="w-6 h-6 flex items-center justify-center" style={{ color: 'var(--text-muted)' }}><Minus size={11} /></button>
                        <span className="font-bold text-sm w-5 text-center">{item.quantite}</span>
                        <button onClick={() => updateCartQty(item.product_id, 1)} className="w-6 h-6 flex items-center justify-center" style={{ color: 'var(--teal)' }}><Plus size={11} /></button>
                      </div>
                      <span className="font-bold text-sm price-gold">{(item.prix_unitaire * item.quantite).toFixed(2)}€</span>
                      <button onClick={() => removeFromCart(item.product_id)} className="ml-auto p-1.5 rounded" style={{ color: '#e53e3e' }}><Trash2 size={13} /></button>
                    </div>
                  </div>
                </div>
              ))}

              {/* Économies */}
              {cartSavings > 0 && (
                <div className="flex items-center gap-2 px-3 py-2 rounded-xl"
                  style={{ background: profileCfg?.bg, border: `1px solid ${profileCfg?.color}25` }}>
                  <Tag size={14} style={{ color: profileCfg?.color }} />
                  <p className="text-sm font-semibold" style={{ color: profileCfg?.color }}>
                    Économie {profileCfg?.label} : -{cartSavings.toFixed(2)}€
                  </p>
                </div>
              )}

              {/* Infos client */}
              <div className="space-y-3 pt-2" style={{ borderTop: '1px solid var(--border)' }}>
                <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: 'var(--text-muted)' }}>Vos informations <span style={{ color: '#e53e3e' }}>* Obligatoires</span></p>
                <Input placeholder="Nom complet *" value={clientInfo.nom} onChange={e => setClientInfo(i => ({ ...i, nom: e.target.value }))} data-testid="boutique-nom" />
                <Input placeholder="Email *" type="email" value={clientInfo.email} onChange={e => setClientInfo(i => ({ ...i, email: e.target.value }))} data-testid="boutique-email" />
                <Input placeholder="Téléphone * (ex: 06 12 34 56 78)" type="tel" value={clientInfo.telephone}
                  onChange={e => setClientInfo(i => ({ ...i, telephone: e.target.value }))}
                  style={{ borderColor: !clientInfo.telephone ? 'rgba(229,62,62,0.4)' : undefined }} />
                <Input placeholder="Adresse de livraison complète *" value={clientInfo.adresse}
                  onChange={e => setClientInfo(i => ({ ...i, adresse: e.target.value }))}
                  style={{ borderColor: !clientInfo.adresse ? 'rgba(229,62,62,0.4)' : undefined }} />
                {user && !user.telephone && (
                  <p className="text-xs p-2 rounded-lg" style={{ background: 'rgba(229,62,62,0.05)', color: '#e53e3e', border: '1px solid rgba(229,62,62,0.2)' }}>
                    💡 <Link to="/mon-compte" onClick={() => setShowCart(false)} style={{ color: '#e53e3e', fontWeight: 600 }}>Complétez votre profil</Link> pour pré-remplir automatiquement vos informations.
                  </p>
                )}
              </div>
            </div>

            {/* Footer panier */}
            <div className="p-5 space-y-3" style={{ borderTop: '1px solid var(--border)' }}>
              <div className="flex justify-between items-center">
                <span className="text-sm" style={{ color: 'var(--text-muted)' }}>Total TTC</span>
                <span className="text-xl font-bold price-gold">{cartTotal.toFixed(2)}€</span>
              </div>
              <Button
                onClick={handleCheckout}
                disabled={checkingOut || cart.length === 0}
                className="w-full font-bold py-3"
                style={{ background: 'var(--teal)', color: '#fff' }}
                data-testid="boutique-checkout-btn"
              >
                {checkingOut ? 'Redirection…' : (
                  <><Check size={16} className="mr-2" /> Payer {cartTotal.toFixed(2)}€ — Stripe sécurisé</>
                )}
              </Button>
              <p className="text-xs text-center" style={{ color: 'var(--text-muted)' }}>
                Paiement 100% sécurisé · Livraison selon conditions
              </p>
            </div>
          </div>
        </div>
      )}

      <MobileStickyBar />
      <Footer />
    </div>
  );
}
